import React, { useRef, useEffect, useMemo, useCallback, useState, } from "react";
import useAuthUser from "../hooks/useAuthUser";
import useChat from "../hooks/useChat";
import { auth } from "../firebase";
import { postChatMessage } from "../services/chatService";
import { executeCommand } from "../utils/commands";
import { useNotifications } from "../hooks/notificationsContext";
import OnlinePlayersButton from "./OnlinePlayersButton";
import CommandGuideModal from './CommandGuideModal'; // Adjust path if needed

function ChatPanel({ onSelectedPlayer }) {
  const userData = useAuthUser();
  const { addNotification } = useNotifications();
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const {
    activeChatTab,
    setActiveChatTab,
    chatInput,
    setChatInput,
    chatMessages,
  } = useChat(userData);

  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const isStaff = userData?.userLevel === "Moderator" || userData?.userLevel === "Admin";
  const isSystemTabLocked = activeChatTab === "System" && !isStaff;

  const resolveChannelName = useCallback(
    (tab) => {
      switch (tab) {
        case "Company": return userData?.userCompanyTicker || "Global";
        case "Region": return userData?.userRegion || "false";
        default: return tab;
      }
    },
    [userData]
  );

  useEffect(() => {
    if (!activeChatTab) setActiveChatTab("Global");
  }, [activeChatTab, setActiveChatTab]);

  const visibleMessages = useMemo(() => {
    return chatMessages[activeChatTab] || [];
  }, [chatMessages, activeChatTab]);

  useEffect(() => {
    if (!chatContainerRef.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [visibleMessages]);

  const handleChatSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const cleanInput = chatInput.trim();
      if (!cleanInput || !auth.currentUser) return;

      const channelName = resolveChannelName(activeChatTab);

      if (channelName === "System" && !isStaff) {
        addNotification("System channel is read-only.", "error");
        setChatInput("");
        return;
      }

      setChatInput("");

      if (cleanInput.startsWith("/")) {
        const handled = await executeCommand(cleanInput, {
          uid: auth.currentUser.uid,
          userData,
          addNotification,
          tab: channelName,
        });
        if (handled) return;
      }

      try {
        await postChatMessage({
          uid: auth.currentUser.uid,
          tab: channelName,
          message: cleanInput,
          pip: false,
        });
      } catch (err) {
        addNotification("Error sending message: " + err.message, "error");
      }
    },
    [chatInput, activeChatTab, userData, isStaff, setChatInput, addNotification, resolveChannelName]
  );

  const handleNameClick = (playerName) => {
    if (onSelectedPlayer) onSelectedPlayer(playerName);
  };

  const renderMessageWithMentions = (text) => {
    const mentionRegex = /@(\w+)\b/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(<span key={lastIndex}>{text.slice(lastIndex, match.index)}</span>);
      }
      const name = match[1];
      parts.push(
        <span 
          key={match.index} 
          style={{ color: "#50e3c2", cursor: "pointer", textDecoration: "underline" }} 
          onClick={() => handleNameClick(name)}
        >
          @{name}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) parts.push(<span key={lastIndex}>{text.slice(lastIndex)}</span>);
    return parts;
  };

return (
  <section 
    style={{ 
      background: "rgba(0, 15, 15, 0.8)", 
      border: "1px solid rgba(80, 227, 194, 0.2)",
      display: "flex",
      flexDirection: "column",
      fontFamily: "'JetBrains Mono', monospace",
      height: "100%" 
    }}
  >
<div style={{ display: "flex", borderBottom: "1px solid rgba(255,255,255,0.05)", background: "rgba(0,0,0,0.2)", alignItems: "stretch" }}>
  {["System", "Global", "Trade", "Company", "Region"].map((tab) => {
if (tab === "Company" && (userData?.userCompanyTicker === "NONE" || !userData?.userCompanyTicker)) return null;

if (tab === "Region" && (userData?.userCompanyTicker === "NONE" || !userData?.userCompanyTicker || userData?.userRegion === "false")) return null;

    const isActive = activeChatTab === tab;
    return (
      <div 
        key={tab}
        onClick={() => setActiveChatTab(tab)}
        style={{
          flex: 1,
          textAlign: "center",
          padding: "10px 0",
          fontSize: "0.6rem",
          letterSpacing: "1px",
          cursor: "pointer",
          borderBottom: `2px solid ${isActive ? "#50e3c2" : "transparent"}`,
          color: isActive ? "#50e3c2" : "#555",
          transition: "all 0.2s ease",
          textTransform: "uppercase"
        }}
      >
        {tab}
      </div>
    );
  })}

  <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
<OnlinePlayersButton 
    isTab={true}
    currentUserName={userData?.userDisplayName || userData?.username || "null-error"}
    onSelectedPlayer={(name) => {
      setChatInput(`@${name} `);
      const inputEl = document.querySelector(".chat-input") || document.querySelector("textarea");
      if (inputEl) inputEl.focus();
    }} 
  />
  </div>
</div>

<div 
  ref={chatContainerRef} 
  style={{ 
    flex: 1, 
    minHeight: 0,
    overflowY: "auto", 
    padding: "15px", 
    fontSize: "0.85rem",
    scrollbarWidth: "thin",
    scrollbarColor: "#222 transparent"
  }}
>
{visibleMessages.map((msg, idx) => {
  const isMentioned = userData?.userDisplayName && new RegExp(`@${userData.userDisplayName}\\b`).test(msg.chatMessage);
  const isSystemMsg = activeChatTab === "System";

  const levelClass = {
    Basic: "basic",
    Preferred: "preferred",
    Premium: "premium",
    Power: "power",
    Moderator: "moderator",
    Admin: "admin"
  }[msg.chatLevel] || "";

  return (
    <div key={idx} style={{ 
      marginBottom: "8px", 
      padding: isMentioned ? "6px 10px" : "2px 0",
      background: isMentioned ? "rgba(80, 227, 194, 0.1)" : "transparent",
      borderLeft: isMentioned ? "2px solid #50e3c2" : "none",
    }}>
      <span style={{ color: "#777", fontSize: "0.65rem", marginRight: "8px" }}>
        {msg.chatCurrentTime}
      </span>
      
      {!isSystemMsg && (
        <>
          {msg.chatPIP ? (
            <span style={{ 
              color: "#50e3c2", 
              fontWeight: "bold", 
              fontSize: "0.85rem",
              textShadow: "0 0 8px rgba(80, 227, 194, 0.5)" 
            }}>
              PIP
            </span>
          ) : (
            <span
              onClick={() => handleNameClick(msg.chatSender)}
              className={`chat-username ${levelClass}`}
              style={{ 
                cursor: "pointer", 
                fontWeight: "600", 
                fontSize: "0.85rem",
              }}
            >
              {msg.chatSender}
            </span>
          )}

          {!msg.chatPIP && msg.chatTicker && msg.chatTicker !== "NONE" && (
            <span style={{ color: "#F8F9FA", fontSize: "0.65rem", opacity: 0.8 }}> ${msg.chatTicker}</span>
          )}

          <span style={{ color: "#888", margin: "0 4px" }}>:</span>
        </>
      )}

      <span style={{ 
        color: isSystemMsg ? "#50e3c2" : "#F8F9FA", 
        lineHeight: "1.5",
        textShadow: isSystemMsg ? "0 0 5px rgba(80, 227, 194, 0.2)" : "none"
      }}>
        {renderMessageWithMentions(msg.chatMessage)}
      </span>
    </div>
  );
})}
        <div ref={messagesEndRef}></div>
      </div>

      <form 
        onSubmit={handleChatSubmit}
        style={{ 
          display: "flex", 
          background: "rgba(0, 0, 0, 0.4)", 
          borderTop: "1px solid rgba(80, 227, 194, 0.2)",
          alignItems: "stretch"
        }}
      >
        <div style={{ 
          padding: "12px", 
          color: "#50e3c2", 
          fontSize: "0.7rem", 
          background: "rgba(80, 227, 194, 0.05)",
          borderRight: "1px solid rgba(80, 227, 194, 0.1)",
          display: "flex",
          alignItems: "center"
        }}>
          {activeChatTab.toUpperCase()}
        </div>
        <button
      type="button"
      onClick={() => setIsGuideOpen(true)}
      style={{
        background: "rgba(80, 227, 194, 0.15)",
        border: "1px solid rgba(80, 227, 194, 0.3)",
        color: "#50e3c2",
        borderRadius: "4px",
        width: "18px",
        height: "18px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.6rem",
        cursor: "pointer",
        padding: 0,
        fontWeight: "bold"
      }}
      title="View Commands"
    >
      ?
    </button>
        <input
          type="text"
          placeholder={isSystemTabLocked ? "READ ONLY" : "TYPE A MESSAGE..."}
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          disabled={isSystemTabLocked}
          style={{
            flex: 1,
            background: "transparent",
            border: "none",
            color: isSystemTabLocked ? "#333" : "#fff",
            padding: "12px 15px",
            fontSize: "0.8rem",
            fontFamily: "inherit",
            outline: "none"
          }}
        />
        <button 
          type="submit" 
          disabled={isSystemTabLocked}
          style={{
            padding: "0 25px",
            background: isSystemTabLocked ? "transparent" : "#50e3c2",
            color: isSystemTabLocked ? "#333" : "#000",
            border: "none",
            fontFamily: "inherit",
            fontSize: "0.65rem",
            fontWeight: "bold",
            cursor: isSystemTabLocked ? "default" : "pointer",
            textTransform: "uppercase"
          }}
        >
          {isSystemTabLocked ? "LOCKED" : "Send"}
        </button>
      </form>
<CommandGuideModal 
  isOpen={isGuideOpen} 
  onClose={() => setIsGuideOpen(false)} 
  userLevel={userData?.userLevel}
/>
    </section>
    
  );
}

export default React.memo(ChatPanel);